import React, { useState } from 'react';
import { Scale } from 'lucide-react';
import { Modal } from '@components/Dialog';
import { Button } from '@components/Button';
import { DateInputBR } from '@components/DateInputBR';
import { MoneyInput } from '@components/MoneyInput';
import accountService from '@services/accountService';
import type { Account, AccountBalance } from '@/types/finance';
import { todayISO } from '@/utils/formatters';
import { Required, labelCls } from './financeBits';

interface Props {
  account: Account;
  onClose: () => void;
  onSaved: (balance: AccountBalance) => void;
  onError: (err: unknown) => void;
}

/**
 * Ajuste de saldo (POST /accounts/:id/balances, upsert por data): "neste dia a conta fechou com
 * R$ X". O saldo pode ser negativo (cheque especial): o usuário marca "saldo negativo".
 */
export const BalanceAdjustModal: React.FC<Props> = ({ account, onClose, onSaved, onError }) => {
  const [saving, setSaving] = useState(false);
  const [date, setDate] = useState(todayISO());
  const [amount, setAmount] = useState<number | null>(null);
  const [negative, setNegative] = useState(false);
  const [notes, setNotes] = useState('');

  const canSubmit = !saving && !!date && amount !== null;

  const save = async () => {
    if (!canSubmit || amount === null) return;
    setSaving(true);
    try {
      onSaved(
        await accountService.setBalance(account.id, {
          balance_date: date,
          amount_cents: negative ? -Math.abs(amount) : Math.abs(amount),
          notes: notes.trim() || null,
        }),
      );
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="sm" closeOnBackdrop={!saving}>
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
        <Scale size={18} className="text-slate-500" />
        <h2 className="text-h3">Ajustar saldo</h2>
      </div>
      <div className="px-6 py-4 max-h-[70vh] overflow-y-auto custom-scrollbar space-y-3">
        <p className="text-small">
          Informe o saldo real da conta <strong>{account.name}</strong> numa data (o do extrato do banco). A partir desse dia o
          saldo passa a ser calculado desse valor em diante; já existe um ajuste nessa data, ele é substituído.
        </p>
        <div>
          <label className={labelCls}>
            Data
            <Required />
          </label>
          <DateInputBR className="input-field w-full" value={date} onChange={setDate} />
        </div>
        <div>
          <label className={labelCls}>
            Saldo nesta data
            <Required />
          </label>
          <MoneyInput value={amount} onChange={setAmount} autoFocus />
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer mt-2">
            <input type="checkbox" className="checkbox-field" checked={negative} onChange={(e) => setNegative(e.target.checked)} />
            Saldo negativo (devedor)
          </label>
        </div>
        <div>
          <label className={labelCls}>Observação</label>
          <input className="input-field" maxLength={200} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ex.: conferido no app do banco" />
        </div>
      </div>
      <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={save} loading={saving} disabled={!canSubmit}>
          Registrar
        </Button>
      </div>
    </Modal>
  );
};
